// BroadcastChannel is the cross-tab transport for leader heartbeats and session decisions.
export const SESSION_CHANNEL_NAME = "session-sync";
export const SESSION_LEADER_TAB_KEY = "mercury:session-leader-tab";
export const SESSION_HEARTBEAT_MS = 2000;
export const SESSION_HEARTBEAT_STALE_MS = 5000;
export const SESSION_ACTIVE_WINDOW_MS = 60 * 1000;
// export const SESSION_ACTIVE_WINDOW_MS = 10 * 1000; // for testing locally because MAS token expires after 12 hrs
// Automatically extends the session when the user is active and the token is close to expiry.
export const SESSION_AUTO_EXTEND_THRESHOLD_MS = 5 * 60 * 1000;
// export const SESSION_AUTO_EXTEND_THRESHOLD_MS = 60 * 1000; // for testing locally with EXPIRESINTOKEN=180 on UMS
// Warns users when their session is about to expire.
export const SESSION_MODAL_THRESHOLD_MS = 2 * 60 * 1000;
// export const SESSION_MODAL_THRESHOLD_MS = 30 * 1000; // for testing locally with EXPIRESINTOKEN=180 on UMS
// Ensures jitter never pushes refresh past the token's expiry time.
export const SESSION_REFRESH_JITTER_MAX_MS = 800;
// Once the token has been expired for this long every tab logs itself out.
// The grace covers a leader-tab refresh that lands in the final second and a
// follower whose cookie read lags the leader's write by a tick.
export const SESSION_EXPIRY_GRACE_MS = 3000;

export type SessionSyncMessageType = "HEARTBEAT" | "LEADER_TAB_CHANGE" | "SESSION_EXTEND" | "SESSION_EXPIRE";

export interface SessionSyncMessage {
    type: SessionSyncMessageType;
    tabId: string;
    timestamp: number;
    leaderTabId?: string | null;
}

export interface SessionLeaderTabState {
    tabId: string;
    timestamp: number;
    lastActivity: number;
}

export function getOrCreateSessionTabId(): string {
    const key = "mercury:session-tab-id";
    const existing = globalThis.sessionStorage.getItem(key);
    if (existing) {
        return existing;
    }

    const tabId = `tab-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    globalThis.sessionStorage.setItem(key, tabId);
    return tabId;
}

export function readLeaderTabState(): SessionLeaderTabState | null {
    try {
        const raw = globalThis.localStorage.getItem(SESSION_LEADER_TAB_KEY);
        if (!raw) {
            return null;
        }

        const parsed = JSON.parse(raw) as SessionLeaderTabState;
        if (!parsed?.tabId || typeof parsed?.timestamp !== "number") {
            return null;
        }

        return parsed;
    } catch (error) {
        console.warn("Unable to read leader tab state", error);
        return null;
    }
}

export function writeLeaderTabState(leaderState: SessionLeaderTabState): void {
    try {
        globalThis.localStorage.setItem(SESSION_LEADER_TAB_KEY, JSON.stringify(leaderState));
    } catch (error) {
        console.warn("Unable to persist leader tab state", error);
    }
}

export function clearLeaderTabState(currentTabId: string): void {
    try {
        const existing = readLeaderTabState();
        if (existing?.tabId === currentTabId) {
            globalThis.localStorage.removeItem(SESSION_LEADER_TAB_KEY);
        }
    } catch (error) {
        console.warn("Unable to clear leader tab state", error);
    }
}

// 2 second timeout during session retry to prevent redirect loops.
let sessionRetryTimeoutId: NodeJS.Timeout | null = null;

export function isSessionRetryInProgress(): boolean {
    return sessionRetryTimeoutId !== null;
}

export function startSessionRetry(): void {
    if (sessionRetryTimeoutId) {
        clearTimeout(sessionRetryTimeoutId);
    }
    sessionRetryTimeoutId = setTimeout(() => {
        sessionRetryTimeoutId = null;
    }, 2000);
}

// Shared "refresh in flight" flag. Both the leader-tab auto refresh and the
// session modal's Extend button go through it, so the hard-expiry timer never
// logs a tab out while a refresh request is still outstanding.
let sessionRefreshInProgress = false;

export function isSessionRefreshInProgress(): boolean {
    return sessionRefreshInProgress;
}

export function beginSessionRefresh(): boolean {
    if (sessionRefreshInProgress) {
        return false;
    }
    sessionRefreshInProgress = true;
    return true;
}

export function endSessionRefresh(): void {
    sessionRefreshInProgress = false;
}

// Picks the token that expires last. Undecodable tokens never win.
export function pickLatestExpiringToken(
    tokens: Array<string | undefined>,
    expiryOf: (token?: string) => number | null
): string | undefined {
    return tokens.reduce<string | undefined>((latest, token) => {
        if (!token) {
            return latest;
        }
        const tokenExpiry = expiryOf(token);
        if (tokenExpiry === null) {
            return latest;
        }
        if (!latest) {
            return token;
        }
        return tokenExpiry > (expiryOf(latest) ?? 0) ? token : latest;
    }, undefined);
}
